import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useReducer, useContext } from 'react';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { ExperienceNames, ExperienceReasons } from '../experience-tracker/ExperienceNames';

/** NOTE:
 * The STORAGE_ACCESS and LOGIN_CONFIRMATION subjects mirror the ones in [Confluence Monolith](https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/)
 * If there are changes needed, they should be applied in both places.
 */
export var Subjects = /*#__PURE__*/function (Subjects) {
  Subjects["STORAGE_ACCESS"] = "storage-access";
  Subjects["LOGIN_CONFIRMATION"] = "login-confirmation";
  Subjects["CONTENT_LOAD"] = "content-load";
  return Subjects;
}({});
export var LoadingStatus = /*#__PURE__*/function (LoadingStatus) {
  LoadingStatus["IFRAME_LOADED"] = "iframe-loaded";
  LoadingStatus["IFRAME_UNLOADED"] = "iframe-unloaded";
  LoadingStatus["CONTENT_LOAD_STARTED"] = "content-load-started";
  LoadingStatus["REDIRECTED_TO_LOGIN"] = "redirected-to-login";
  LoadingStatus["RELOAD_EP"] = "reload-ep";
  return LoadingStatus;
}({});

/** Storage Access statuses:
 * These statuses mirror the ones in [Confluence Monolith](confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/embedded-confluence-storage-access.vm)
 * If there are changes needed, they should be applied in both places.
 */
export var StorageAccessStatus = /*#__PURE__*/function (StorageAccessStatus) {
  StorageAccessStatus["GRANTED"] = "granted";
  StorageAccessStatus["REJECTED"] = "rejected";
  StorageAccessStatus["NOT_SUPPORTED"] = "not-supported";
  StorageAccessStatus["ERROR"] = "error";
  return StorageAccessStatus;
}({});

/** Login statuses:
 * These statuses mirror the ones in [Confluence Monolith](confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/embedded-confluence-login-confirmation.vm)
 * If there are changes needed, they should be applied in both places.
 */
export var UserLoginStatus = /*#__PURE__*/function (UserLoginStatus) {
  UserLoginStatus["SUCCESS"] = "success";
  return UserLoginStatus;
}({});

/* NOTE: (Refer this table for understanding the behavior
  on different browsers: https://hello.atlassian.net/wiki/spaces/~288141084/pages/2154391293/Conditions+to+load+EP)
*/
function pageLoadValidationReducer(state, action) {
  //NOTE: We assume that storage access and user login is required at the load of the PageLoadValidation component

  switch (action.subject) {
    case Subjects.STORAGE_ACCESS:
      switch (action.status) {
        case StorageAccessStatus.GRANTED:
          /*If storage access is granted, refresh app, if user is logged in, try to load EP. If not, load Login CTA
           */
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: false,
            contentLoadStarted: false,
            shouldAskStorageAccess: false
          });
        case StorageAccessStatus.REJECTED:
          /*If storage access is reject, it could mean either of the two things:
               1. (For Safari): User has storage access but may not be logged in, Try loggin user in
               2. (For Firefox, Edge): User may be logged in but storage access is not yet granted by the browser
               In both these case, ask user to login.
               */
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: false,
            contentLoadStarted: false,
            shouldAskStorageAccess: false,
            shouldAskToLogin: true
          });
        case StorageAccessStatus.NOT_SUPPORTED:
          /* This case is specific for Chrome where storage access is not supported. 
               So we can skip asking for storage access and direct user to the Login CTA. 
               */
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: false,
            contentLoadStarted: false,
            shouldAskToLogin: true,
            shouldAskStorageAccess: false
          });
        case StorageAccessStatus.ERROR:
          /** To catch any other errors while requesting storage access */
          return _objectSpread({}, state);
      }
      break;
    case Subjects.LOGIN_CONFIRMATION:
      switch (action.status) {
        case UserLoginStatus.SUCCESS:
          /*
               If user has logged in, it means that storage access is granted. Refresh app, Try loading EP
               */
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: false,
            contentLoadStarted: false,
            shouldAskToLogin: false,
            iframeUnloaded: false,
            redirectedToLogin: false
          });
      }
      break;
    case Subjects.CONTENT_LOAD:
      //These states are set only when the user has navigated throught the storage access and login flows. So try to load EP
      switch (action.status) {
        case LoadingStatus.IFRAME_LOADED:
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: true
          });
        case LoadingStatus.CONTENT_LOAD_STARTED:
          return _objectSpread(_objectSpread({}, state), {}, {
            contentLoadStarted: true
          });
        case LoadingStatus.IFRAME_UNLOADED:
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeUnloaded: true
          });
        case LoadingStatus.REDIRECTED_TO_LOGIN:
          return _objectSpread(_objectSpread({}, state), {}, {
            redirectedToLogin: true
          });
        case LoadingStatus.RELOAD_EP:
          return _objectSpread(_objectSpread({}, state), {}, {
            iframeLoaded: false,
            contentLoadStarted: false
          });
      }
      break;
  }
}
export var useValidationState = function useValidationState() {
  var initialState = {
    iframeLoaded: false,
    contentLoadStarted: false,
    shouldAskStorageAccess: true,
    shouldAskToLogin: true,
    redirectedToLogin: false,
    iframeUnloaded: false
  };
  var _useReducer = useReducer(pageLoadValidationReducer, initialState),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    state = _useReducer2[0],
    dispatch = _useReducer2[1];
  var experienceTracker = useContext(ExperienceTrackerContext);
  var messageListener = useCallback(function (messageEvent) {
    var _messageEvent$data;
    var _ref = (_messageEvent$data = messageEvent.data) !== null && _messageEvent$data !== void 0 ? _messageEvent$data : {},
      subject = _ref.subject,
      result = _ref.result;
    if (!subject || !result) {
      return;
    }
    switch (subject) {
      case Subjects.STORAGE_ACCESS:
        switch (result) {
          case StorageAccessStatus.GRANTED:
            experienceTracker.succeed({
              name: ExperienceNames.STORAGE_ACCESS_PAGE
            });
            dispatch({
              subject: Subjects.STORAGE_ACCESS,
              status: StorageAccessStatus.GRANTED
            });
            break;
          case StorageAccessStatus.REJECTED:
            experienceTracker.abort({
              name: ExperienceNames.STORAGE_ACCESS_PAGE,
              reason: ExperienceReasons.STORAGE_ACCESS_REJECTED
            });
            dispatch({
              subject: Subjects.STORAGE_ACCESS,
              status: StorageAccessStatus.REJECTED
            });
            break;
          case StorageAccessStatus.NOT_SUPPORTED:
            experienceTracker.abort({
              name: ExperienceNames.STORAGE_ACCESS_PAGE,
              reason: ExperienceReasons.STORAGE_ACCESS_NOT_SUPPORTED
            });
            dispatch({
              subject: Subjects.STORAGE_ACCESS,
              status: StorageAccessStatus.NOT_SUPPORTED
            });
            break;
          case StorageAccessStatus.ERROR:
            experienceTracker.fail({
              name: ExperienceNames.STORAGE_ACCESS_PAGE,
              error: new Error(ExperienceReasons.STORAGE_ACCESS_ERROR)
            });
            dispatch({
              subject: Subjects.STORAGE_ACCESS,
              status: StorageAccessStatus.ERROR
            });
            break;
        }
        break;
      case Subjects.LOGIN_CONFIRMATION:
        switch (result) {
          case UserLoginStatus.SUCCESS:
            experienceTracker.succeed({
              name: ExperienceNames.LOGIN_PAGE
            });
            dispatch({
              subject: Subjects.LOGIN_CONFIRMATION,
              status: UserLoginStatus.SUCCESS
            });
            break;
        }
        break;
    }
  }, [experienceTracker]);
  useEffect(function () {
    window.addEventListener('message', messageListener);
    return function () {
      return window.removeEventListener('message', messageListener);
    };
  }, [messageListener]);
  return {
    state: state,
    dispatch: dispatch
  };
};